import { log } from '@/lib/logger'
import React, { useState, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { MessagesSquare, Search, User, ThumbsUp, NotebookPen, ArrowLeft, Menu, Archive, CheckCircle2, Copy, Trash2, Radar } from 'lucide-react'
import { TooltipProvider } from '@/components/ui/tooltip'
import { useIsMobile } from '@/hooks/use-mobile'
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '@/components/ui/resizable'
import { Separator } from '@/components/ui/separator'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { ConversationList } from '@/components/conversations/conversation-list'
import { ConversationListSkeleton } from '@/components/conversations/conversation-list-skeleton'
import { ConversationDisplay } from '@/components/conversations/conversation-display'
import { UserProfile } from '@/components/conversations/user-profile'
import { FeedbackForm } from '@/components/conversations/feedback-form'
import { ConversationInstructions } from '@/components/conversations/ConversationInstructions'
import { ConversationTraceability } from '@/components/conversations/conversation-traceability'
import { ConfirmationDialog } from '@/components/shared/confirmation-dialog'
import { PlatformFilters } from '@/components/conversations/platform-filters'
import { WhatsAppLineFilter } from '@/components/conversations/whatsapp-line-filter'
import { resolveSelectedThread } from '@/lib/whatsapp-threads'
import { useToast } from '@/hooks/use-toast'
import { useConversations } from '@/hooks/use-conversations'
import { usePhoneCalls } from '@/hooks/use-phone-calls'
import { useSelectedConversation } from '@/hooks/use-selected-conversation'
import { cn } from '@/lib/utils'
import { transformConversationsToMails, transformPhoneCallsToMails } from '@/lib/conversation-utils'
import { estadoDeLaPestana } from '@/lib/conversation-list-filter'
import type { Mail } from '@/lib/data'
import { conversationsApi } from '@/services/conversationsApi'
import { useInboxChannelCatalog } from '@/hooks/use-inbox-channel-catalog'
import { channelKeyToMailPlatform } from '@/lib/channel-inbox'
import { betterplaceService } from '@/services/betterplaceService'
import { transformBetterplaceListingsToMails } from '@/lib/conversation-utils'

// Oldest loaded date for a source, used to coordinate independently-paginated
// sources (conversations + phone calls) so the merged "all" view never shows
// (then reshuffles) items out of true chronological order.
function getOldestMailTimestamp(mails: Mail[]): number | null {
  if (mails.length === 0) return null
  return Math.min(...mails.map((mail) => new Date(mail.date).getTime()))
}

/** Las etiquetas de la persona que marcan un lead de formulario (Meta / web). */
const ETIQUETAS_FORM_LEADS = 'form-leads,form_leads,formlead,fb_lead,ig_lead'
const RETRASO_BUSQUEDA_MS = 300
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const DEV_MOCK_CONVERSATION: Mail = {
  id: 'mock-conversation-mobile-001',
  user: 'mock-user-mobile-001',
  name: 'Preview IA User',
  email: 'preview.ia.user@example.com',
  subject: 'Cita para visita de piso',
  text: 'Perfecto, la visita queda agendada para el 23 de abril a las 17:30.',
  date: '2026-04-19T21:32:00Z',
  displayDate: '21:32',
  status: 'active',
  platform: 'web',
  unreadCount: 1,
}


// Las cinco pestanas del panel derecho, una vez. Estaban escritas dos veces
// -una para movil y otra para escritorio-, con cinco colores distintos para
// decir lo mismo: "esta elegida". Cinco colores no son cinco significados;
// elegido es `text-primary`, aqui y en el resto de la aplicacion.
const PANELES = [
  { id: 'conversation', icono: MessagesSquare, clave: 'functions.conversation' },
  { id: 'profile', icono: User, clave: 'functions.profile' },
  { id: 'instructions', icono: NotebookPen, clave: 'functions.instructions' },
  { id: 'feedback', icono: ThumbsUp, clave: 'functions.feedback' },
  { id: 'traceability', icono: Radar, clave: 'functions.traceability' },
] as const

type IdDePanel = (typeof PANELES)[number]['id']

function BotonDePanel({
  panel,
  activo,
  soloIcono,
  onElegir,
}: {
  panel: (typeof PANELES)[number]
  activo: boolean
  soloIcono?: boolean
  onElegir: () => void
}) {
  const { t } = useTranslation()
  const Icono = panel.icono
  const nombre = t(panel.clave)
  return (
    <Button
      variant="ghost"
      size={soloIcono ? 'icon-xs' : 'sm'}
      className={cn(
        'shrink-0 gap-2 hover:bg-transparent',
        activo ? 'text-primary hover:text-primary' : 'text-muted-foreground hover:text-foreground',
      )}
      onClick={onElegir}
      aria-label={soloIcono ? nombre : undefined}
    >
      <Icono className="h-4 w-4 shrink-0" />
      {!soloIcono && nombre}
    </Button>
  )
}

export function ConversationsPage() {
  const { toast } = useToast()
  const [activeTab, setActiveTab] = useState<"active" | "archived">("active")
  const [searchTerm, setSearchTerm] = useState("")
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false)
  const [mobileDeleteDialogOpen, setMobileDeleteDialogOpen] = useState(false)
  const [mobileActionsLoading, setMobileActionsLoading] = useState(false)
  const [selectedPlatform, setSelectedPlatform] = useState("all")
  const [rightPanelView, setRightPanelView] = useState<"conversation" | "profile" | "feedback" | "instructions" | "traceability">("conversation")
  const [betterplaceMails, setBetterplaceMails] = useState<Mail[]>([])
  const [betterplaceLoading, setBetterplaceLoading] = useState(false)
  const [betterplaceError, setBetterplaceError] = useState<string | null>(null)
  const isPropertyLeadPlatform = selectedPlatform === 'prospeccion' || selectedPlatform === 'betterplace'

  // Cual de las cinco pestanas esta elegida, y que hace pulsar una. Para una
  // ficha de propiedad no hay conversacion que ensenar, asi que esa pestana
  // lleva al perfil: es la unica excepcion, y vive aqui en vez de repetida en
  // cada boton.
  const panelElegido: IdDePanel =
    isPropertyLeadPlatform && rightPanelView === 'conversation' ? 'profile' : rightPanelView
  const elegirPanel = (id: IdDePanel) =>
    setRightPanelView(id === 'conversation' && isPropertyLeadPlatform ? 'profile' : id)
  // Track active custom filter's channels to know when to include phone calls
  const [activeFilterChannels, setActiveFilterChannels] = useState<string[]>([])
  // Linea de WhatsApp seleccionada en la barra (`phone_number_id`), o null.
  const [selectedLine, setSelectedLine] = useState<string | null>(null)
  const { t } = useTranslation()
  const { lang } = useParams<{ lang: string }>()
  const isMobile = useIsMobile()

  // Normalize status to our simplified states for tab filtering
  const normalizeStatusForTab = (status: string): "active" | "archived" => {
    return status === "archived" ? "archived" : "active"
  }

  // Use selected conversation hook for conversation selection
  const [selectedConversation, setSelectedConversation] = useSelectedConversation()

  // Use real data hooks with WebSocket integration
  const {
    conversations,
    loading,
    error,
    hasMore,
    loadingMore,
    statusCounts,
    refetch,
    loadMore,
    updateStatus,
    removeConversation,
    markAsRead,
    setFilters,
  } = useConversations({ status: estadoDeLaPestana('active') })

  const applyConversationFilters = React.useCallback((filters: {
    source?: string
    phone_number_id?: string
    category?: string
    tags?: string
    tag_ids?: string
    lead_source?: string
    user_tags?: string
  }) => {
    setFilters({
      source: undefined,
      phone_number_id: undefined,
      category: undefined,
      tags: undefined,
      tag_ids: undefined,
      lead_source: undefined,
      user_tags: undefined,
      ...filters,
    })
  }, [setFilters])

  // La pestana viaja al servidor; el canal y la busqueda van aparte.
  React.useEffect(() => {
    setFilters({ status: estadoDeLaPestana(activeTab) })
  }, [activeTab, setFilters])

  // Use phone calls hook
  const { catalog: channelCatalog, pinnedKeys: pinnedChannelKeys, togglePinChannelKey } =
    useInboxChannelCatalog()

  const {
    calls: phoneCalls,
    loading: phoneCallsLoading,
    error: phoneCallsError,
    hasMore: phoneCallsHasMore,
    loadingMore: phoneCallsLoadingMore,
    refetch: phoneCallsRefetch,
    loadMore: phoneCallsLoadMore,
    setFilters: setPhoneCallFilters,
    filters: phoneCallFilters,
  } = usePhoneCalls()

  // Transform conversations and phone calls to mails format
  // Ensure arrays before transformation
  const safeConversations = Array.isArray(conversations) ? conversations : []
  const safePhoneCalls = Array.isArray(phoneCalls) ? phoneCalls : []

  const conversationMails = transformConversationsToMails(safeConversations as never[])
  const phoneCallMails = transformPhoneCallsToMails(safePhoneCalls)
  const shouldUseDevMockConversation =
    import.meta.env.DEV && conversationMails.length === 0 && phoneCallMails.length === 0
  const mockConversationMails = shouldUseDevMockConversation ? [DEV_MOCK_CONVERSATION] : []
  const webConversationMails = [...conversationMails, ...mockConversationMails]

  // Coordinate the "all" view's two independently-paginated sources: a
  // source that still has unfetched pages may have older items waiting that
  // we haven't seen yet, so cap what we show to the more recent of the two
  // frontiers until both have either caught up or run out of pages.
  const conversationsFrontier = hasMore ? getOldestMailTimestamp(webConversationMails) : null
  const callsFrontier = phoneCallsHasMore ? getOldestMailTimestamp(phoneCallMails) : null
  const mergeCutoffTimestamp =
    conversationsFrontier === null && callsFrontier === null
      ? null
      : Math.max(conversationsFrontier ?? -Infinity, callsFrontier ?? -Infinity)
  const mergeLimitingSource: 'conversations' | 'calls' | null =
    mergeCutoffTimestamp === null
      ? null
      : conversationsFrontier === mergeCutoffTimestamp
        ? 'conversations'
        : 'calls'

  const loadBetterplaceListings = useCallback(async () => {
    setBetterplaceLoading(true)
    setBetterplaceError(null)

    try {
      const response = await betterplaceService.listProperties({ page: 1, per_page: 20 })
      const results = Array.isArray(response.results) ? response.results : []
      setBetterplaceMails(transformBetterplaceListingsToMails(results))
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No se pudieron cargar las propiedades Betterplace.'
      setBetterplaceError(message)
      setBetterplaceMails([])
    } finally {
      setBetterplaceLoading(false)
    }
  }, [])

  React.useEffect(() => {
    if (selectedPlatform !== 'betterplace') {
      return
    }

    void loadBetterplaceListings()
  }, [selectedPlatform, loadBetterplaceListings])

  // Filter mails based on selected platform
  let filteredMails = webConversationMails

  if (selectedPlatform === 'phone_call') {
    // Show only phone calls when phone_call filter is selected
    filteredMails = phoneCallMails
  } else if (selectedPlatform === 'betterplace') {
    filteredMails = betterplaceMails
  } else if (selectedPlatform === 'whatsapp') {
    // Show only WhatsApp conversations when whatsapp filter is selected
    filteredMails = webConversationMails.filter((mail) => mail.platform === 'whatsapp')
  } else if (selectedPlatform === 'web' || selectedPlatform === 'form-leads') {
    // Ya vienen filtradas del servidor (`source=Web` / `user_tags`).
    filteredMails = webConversationMails
  } else if (selectedPlatform.startsWith('registry:')) {
    const registryKey = selectedPlatform.slice('registry:'.length)
    filteredMails = webConversationMails.filter(
      (mail) => {
        const sourceKey = String(mail.sourceKey || '').trim().toLowerCase()
        if (sourceKey) {
          return sourceKey === registryKey.trim().toLowerCase()
        }
        // Legacy fallback for rows transformed before sourceKey existed.
        return channelKeyToMailPlatform(registryKey) === mail.platform
      },
    )
  } else if (selectedPlatform === 'all') {
    // Show all conversations (web + phone calls) when 'all' is selected.
    // Hide items older than mergeCutoffTimestamp until the lagging source
    // catches up — see conversationsFrontier/callsFrontier above.
    const combined = [...webConversationMails, ...phoneCallMails]
    filteredMails =
      mergeCutoffTimestamp === null
        ? combined
        : combined.filter((mail) => new Date(mail.date).getTime() >= mergeCutoffTimestamp)
  } else {
    // Custom filter or unknown
    const standardPlatforms = ['all', 'web', 'betterplace', 'phone_call', 'whatsapp', 'form-leads']
    if (!standardPlatforms.includes(selectedPlatform)) {
      // Custom filter active: Check if we need to include phone calls
      const includesPhoneCalls = activeFilterChannels.includes('Phone Call')
      const includesOtherChannels = activeFilterChannels.some((c: string) => c !== 'Phone Call')

      if (includesPhoneCalls && includesOtherChannels) {
        // Mixed filter: include both conversations (filtered by backend) and phone calls
        filteredMails = [...webConversationMails, ...phoneCallMails]
      } else if (includesPhoneCalls) {
        // Only phone calls selected in custom filter
        filteredMails = phoneCallMails
      } else {
        // Only conversation channels - backend filtering already applied
        filteredMails = webConversationMails
      }
    } else {
      // Fallback for unhandled standard platforms
      filteredMails = webConversationMails
    }
  }

  // Sort by date
  const mails = [...filteredMails].sort((a, b) =>
    new Date(b.date).getTime() - new Date(a.date).getTime()
  )
  const listLoading =
    loading ||
    (selectedPlatform === 'betterplace' && betterplaceLoading) ||
    (phoneCallsLoading && (selectedPlatform === 'phone_call' || selectedPlatform === 'all'))
  const listError =
    (selectedPlatform === 'betterplace' ? betterplaceError : error) ||
    (phoneCallsError && (selectedPlatform === 'phone_call' || selectedPlatform === 'all')
      ? phoneCallsError
      : null)
  const listHasMore =
    selectedPlatform === 'betterplace'
      ? false
      : selectedPlatform === 'phone_call'
        ? phoneCallsHasMore
        : selectedPlatform === 'all'
          ? (hasMore || phoneCallsHasMore)
          : hasMore
  const listLoadingMore =
    selectedPlatform === 'betterplace'
      ? false
      : selectedPlatform === 'phone_call'
        ? phoneCallsLoadingMore
        : selectedPlatform === 'all'
          ? (loadingMore || phoneCallsLoadingMore)
          : loadingMore
  // Los contadores de pestana salen del servidor (respetan canal y busqueda),
  // no de las filas cargadas: antes "Archivadas (0)" con 34 en la base.
  // Las llamadas no pasan por ese endpoint: se suman las cargadas.
  const incluyeLlamadas = selectedPlatform === 'phone_call' || selectedPlatform === 'all'
  const llamadasPorPestana = incluyeLlamadas ? phoneCallMails : []
  const activeCount =
    (selectedPlatform === 'betterplace'
      ? betterplaceMails.filter((m) => normalizeStatusForTab(m.status) === 'active').length
      : selectedPlatform === 'phone_call'
        ? 0
        : (statusCounts.active ?? 0) + (statusCounts.pending ?? 0) + (statusCounts.needs_review ?? 0) + (statusCounts.completed ?? 0)) +
    llamadasPorPestana.filter((m) => normalizeStatusForTab(m.status) === 'active').length
  const archivedCount =
    (selectedPlatform === 'betterplace'
      ? betterplaceMails.filter((m) => normalizeStatusForTab(m.status) === 'archived').length
      : selectedPlatform === 'phone_call'
        ? 0
        : (statusCounts.archived ?? 0)) +
    llamadasPorPestana.filter((m) => normalizeStatusForTab(m.status) === 'archived').length




  // Get selected conversation
  const selectedMail = React.useMemo(
    // Busca por la cabeza de la tarjeta y por sus hilos hermanos: al cambiar de
    // linea, el id elegido no es el de ninguna fila de la lista.
    () => resolveSelectedThread(mails, selectedConversation.selected),
    [mails, selectedConversation.selected],
  )
  const isMockSelectedConversation = Boolean(selectedMail?.id?.startsWith('mock-'))

  const handleOpenRelatedConversation = React.useCallback((conversationId: string) => {
    setSelectedPlatform('whatsapp')
    setRightPanelView('conversation')
    setSelectedConversation({ selected: conversationId })
  }, [setSelectedConversation])

  const handleLoadMore = useCallback(() => {
    if (selectedPlatform === 'phone_call') {
      phoneCallsLoadMore()
    } else if (selectedPlatform === 'all') {
      // Only advance the source that's limiting how far back we can safely
      // show the merged feed (see mergeLimitingSource above).
      if (mergeLimitingSource === 'calls') {
        phoneCallsLoadMore()
      } else {
        loadMore()
      }
    } else {
      loadMore()
    }
  }, [selectedPlatform, mergeLimitingSource, loadMore, phoneCallsLoadMore])


  // Handle conversation deletion
  const handleDeleted = useCallback(async (conversationId: string) => {
    try {
      // Clear selection if the deleted conversation was selected
      if (selectedConversation.selected === conversationId) {
        setSelectedConversation({ selected: null })
      }
      // Remove the conversation from the local list and delete from server
      await removeConversation(conversationId)
    } catch (error) {
      // Only log actual errors, not "conversation not found" cases
      const errorMessage = error instanceof Error ? error.message : String(error)
      if (!errorMessage.includes('Conversation not found')) {
        // Optionally show a toast notification or error message
      }
    }
  }, [selectedConversation.selected, setSelectedConversation, removeConversation])

  // Handle conversation archiving
  const handleArchived = useCallback(async (conversationId: string) => {
    try {
      // Update the conversation status to archived
      await updateStatus(conversationId, 'archived')

      // Clear selection if the archived conversation was selected
      if (selectedConversation.selected === conversationId) {
        setSelectedConversation({ selected: null })
      }
    } catch {
      // Ignorar errores de carga
    }
  }, [updateStatus, selectedConversation.selected, setSelectedConversation])

  // Handle conversation activation (unarchiving)
  const handleActivated = useCallback(async (conversationId: string) => {
    try {
      // Update the conversation status to active
      await updateStatus(conversationId, 'active')

      // Clear selection if the activated conversation was selected
      if (selectedConversation.selected === conversationId) {
        setSelectedConversation({ selected: null })
      }
    } catch {
      // Ignorar errores de carga
    }
  }, [updateStatus, selectedConversation.selected, setSelectedConversation])

  // Handle conversation status change
  const handleStatusChange = useCallback(async (conversationId: string, newStatus: 'active' | 'needs_review' | 'archived' | 'completed') => {
    log.debug('📝 ConversationsPage handleStatusChange:', {
      conversationId,
      newStatus,
      updateStatusFunction: typeof updateStatus
    })

    try {
      // Use the status directly as it matches the backend
      await updateStatus(conversationId, newStatus)
      log.debug('✅ ConversationsPage status update completed')
    } catch (error) {
      console.error('❌ ConversationsPage error updating conversation status:', error)
    }
  }, [updateStatus])

  const handleCopyConversationIdMobile = useCallback(async () => {
    if (!selectedMail?.id) return
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(selectedMail.id)
      } else {
        const textArea = document.createElement('textarea')
        textArea.value = selectedMail.id
        textArea.style.position = 'fixed'
        textArea.style.left = '-999999px'
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
      }
      toast({ title: t('functions.conversation_id_copied') || 'Conversation ID copied to clipboard' })
    } catch (error) {
      toast({
        title: t('functions.conversation_id_copied') || 'Failed to copy conversation ID',
        description: error instanceof Error ? error.message : String(error),
        variant: 'destructive',
      })
    }
  }, [selectedMail?.id, t, toast])

  const handleArchiveToggleMobile = useCallback(async () => {
    if (!selectedMail?.id || mobileActionsLoading) return
    if (isMockSelectedConversation) {
      toast({ title: 'Mock conversation', description: 'Esta conversación es de prueba.' })
      return
    }

    setMobileActionsLoading(true)
    try {
      if (selectedMail.platform === 'phone_call') {
        if (selectedMail.status === 'archived') {
          await conversationsApi.unarchivePhoneCall(selectedMail.id)
          toast({ title: 'Conversation activated' })
        } else {
          await conversationsApi.archivePhoneCall(selectedMail.id)
          toast({ title: 'Conversation archived' })
        }
        phoneCallsRefetch()
        setSelectedConversation({ selected: null })
      } else if (selectedMail.status === 'archived') {
        await handleActivated(selectedMail.id)
        toast({ title: 'Conversation activated' })
      } else {
        await handleArchived(selectedMail.id)
        toast({ title: 'Conversation archived' })
      }
    } catch (error) {
      toast({
        title: selectedMail.status === 'archived' ? 'Failed to activate conversation' : 'Failed to archive conversation',
        description: error instanceof Error ? error.message : String(error),
        variant: 'destructive',
      })
    } finally {
      setMobileActionsLoading(false)
    }
  }, [selectedMail, mobileActionsLoading, isMockSelectedConversation, toast, phoneCallsRefetch, setSelectedConversation, handleActivated, handleArchived])

  const handleDeleteMobile = useCallback(async () => {
    if (!selectedMail?.id || mobileActionsLoading) return
    if (isMockSelectedConversation) {
      setSelectedConversation({ selected: null })
      setMobileDeleteDialogOpen(false)
      return
    }

    setMobileActionsLoading(true)
    try {
      if (selectedMail.platform === 'phone_call') {
        await conversationsApi.deletePhoneCall(selectedMail.id)
        phoneCallsRefetch()
      } else {
        await handleDeleted(selectedMail.id)
      }
      toast({ title: t('functions.deleted_success') || 'Conversation deleted' })
      setSelectedConversation({ selected: null })
      setMobileDeleteDialogOpen(false)
    } catch (error) {
      toast({
        title: t('functions.deleted_error') || 'Failed to delete',
        description: error instanceof Error ? error.message : String(error),
        variant: 'destructive',
      })
    } finally {
      setMobileActionsLoading(false)
    }
  }, [selectedMail, mobileActionsLoading, isMockSelectedConversation, setSelectedConversation, phoneCallsRefetch, handleDeleted, t, toast])

  // La busqueda la hace el servidor (nombre, telefono, email, titulo o
  // cualquier mensaje). Un UUID pide esa conversacion concreta. Con retraso
  // para no lanzar una peticion por tecla.
  React.useEffect(() => {
    const trimmedSearch = searchTerm.trim()
    const temporizador = setTimeout(() => {
      if (trimmedSearch && UUID.test(trimmedSearch)) {
        setFilters({ conversationId: trimmedSearch, search: undefined })
      } else {
        setFilters({ conversationId: undefined, search: trimmedSearch || undefined })
      }
    }, trimmedSearch ? RETRASO_BUSQUEDA_MS : 0)
    return () => clearTimeout(temporizador)
  }, [searchTerm, setFilters])

  // Load conversations and phone calls based on platform filter
  React.useEffect(() => {
    if (selectedPlatform.startsWith('registry:')) {
      const key = selectedPlatform.slice('registry:'.length)
      applyConversationFilters({ source: key })
      setActiveFilterChannels([])
      if (Object.keys(phoneCallFilters).length > 0) {
        setPhoneCallFilters({})
      }
      return
    }

    const standardPlatforms = ['all', 'web', 'betterplace', 'phone_call', 'whatsapp', 'form-leads']

    // Only reset filters if selecting a standard platform
    // Custom filters handle their own setFilters() call in onApplyFilter
    if (standardPlatforms.includes(selectedPlatform)) {
      if (selectedPlatform === 'betterplace') {
        applyConversationFilters({ source: 'Betterplace' })
      } else if (selectedPlatform === 'whatsapp') {
        // La linea elegida viaja al backend; sin ella se ven todos los numeros.
        applyConversationFilters({
          source: 'WhatsApp',
          phone_number_id: selectedLine ?? undefined,
        })
      } else if (selectedPlatform === 'web') {
        applyConversationFilters({ source: 'Web' })
      } else if (selectedPlatform === 'form-leads') {
        applyConversationFilters({ user_tags: ETIQUETAS_FORM_LEADS })
      } else {
        applyConversationFilters({})
      }
      setActiveFilterChannels([]) // Reset custom filter channels
      // Prevent infinite loop: Only reset phone calls filters if they are not already empty
      if (Object.keys(phoneCallFilters).length > 0) {
        setPhoneCallFilters({}) // Reset phone call filters
      }
    }

    // Load phone calls only when needed
    if (selectedPlatform === 'phone_call' || selectedPlatform === 'all') {
      phoneCallsRefetch()
    }

  }, [applyConversationFilters, selectedPlatform, selectedLine, phoneCallsRefetch, phoneCallFilters, setPhoneCallFilters])

  // Salir de WhatsApp borra la linea elegida: es un filtro de ese canal.
  React.useEffect(() => {
    if (selectedPlatform !== 'whatsapp' && selectedLine !== null) {
      setSelectedLine(null)
    }
  }, [selectedPlatform, selectedLine])

  // Selected conversation is available via mail.selected

  // ── MOBILE: vista detalle ──
  if (isMobile && selectedConversation.selected) {
    // Guard: mail not yet loaded
    if (!selectedMail) {
      const isStillLoading = listLoading

      if (!isStillLoading) {
        return (
          <TooltipProvider delayDuration={0}>
            <div className="flex h-full flex-col">
              <div className="flex h-12 shrink-0 items-center gap-2 border-b px-3">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSelectedConversation({ selected: null })}
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <span className="truncate text-sm font-medium">{t('functions.conversations')}</span>
              </div>
              <div className="flex min-h-0 flex-1 items-center justify-center p-4 text-center text-sm text-muted-foreground">
                {t('functions.no_conversations')}
              </div>
            </div>
          </TooltipProvider>
        )
      }

      return (
        <div className="flex h-full items-center justify-center">
          <div className="text-center">
            <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-primary" />
            <p className="mt-4 text-sm text-muted-foreground">{t('functions.loading')}</p>
          </div>
        </div>
      )
    }
    return (
      <TooltipProvider delayDuration={0}>
        <div className="flex h-full flex-col">
        {/* Mobile top bar in one line: back + ai badge + tabs + actions */}
        <div className="flex h-12 shrink-0 items-center gap-1 border-b px-1">
          <Button
            variant="ghost"
            size="icon-xs"
            className="shrink-0"
            onClick={() => setSelectedConversation({ selected: null })}
            aria-label={t('functions.conversations')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Badge
            variant="outline"
            className={cn(
              "h-7 shrink-0 px-2 py-0 text-[11px]",
              selectedMail?.platform === 'phone_call'
                ? "bg-warning/10 text-warning hover:bg-warning/20"
                : "bg-primary/10 text-primary hover:bg-primary/20"
            )}
          >
            {selectedMail?.platform === 'phone_call' ? 'Post llamada' : (t('functions.ai_active') || 'AI Active')}
          </Badge>
          <div className="min-w-0 flex-1 overflow-x-auto">
            <div className="flex w-max items-center gap-0.5 pr-1">
              {PANELES.map((panel) => {
                if (panel.id === 'traceability' && isPropertyLeadPlatform) return null
                return (
                  <BotonDePanel
                    key={panel.id}
                    panel={panel}
                    soloIcono
                    activo={panelElegido === panel.id}
                    onElegir={() => elegirPanel(panel.id)}
                  />
                )
              })}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-0.5">
            <Button
              variant="ghost"
              size="icon-xs"
              className="text-muted-foreground hover:text-foreground"
              onClick={() => { void handleCopyConversationIdMobile() }}
              aria-label={t('functions.copy_conversation_id') || 'Copy conversation ID'}
              disabled={!selectedMail?.id}
            >
              <Copy className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon-xs"
              className="text-muted-foreground hover:text-foreground"
              onClick={() => { void handleArchiveToggleMobile() }}
              aria-label={selectedMail?.status === 'archived' ? 'Activate conversation' : 'Archive conversation'}
              disabled={!selectedMail?.id || mobileActionsLoading}
            >
              <Archive className={cn("h-3.5 w-3.5", selectedMail?.status === 'archived' && "rotate-180")} />
            </Button>
            <Button
              variant="ghost"
              size="icon-xs"
              className="text-muted-foreground hover:text-foreground"
              onClick={() => setMobileDeleteDialogOpen(true)}
              aria-label="Delete conversation"
              disabled={!selectedMail?.id || mobileActionsLoading}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {/* Detail content */}
        <div className="min-h-0 flex-1 overflow-hidden">
          {(isPropertyLeadPlatform || rightPanelView === "profile") && (
            <UserProfile
              mail={selectedMail}
              translations={t}
              isPropertyLead={isPropertyLeadPlatform}
              onOpenConversation={handleOpenRelatedConversation}
            />
          )}
          {!isPropertyLeadPlatform && rightPanelView === "conversation" && (
            <ConversationDisplay
              conversation={selectedMail}
              translations={t}
              onDeleted={handleDeleted}
              onArchived={handleArchived}
              onActivated={handleActivated}
              markAsRead={markAsRead}
              onPhoneCallArchived={() => phoneCallsRefetch()}
            />
          )}
          {rightPanelView === "feedback" && (
            <FeedbackForm mail={selectedMail} />
          )}
          {rightPanelView === "instructions" && (
            <ConversationInstructions mail={selectedMail} />
          )}
          {rightPanelView === "traceability" && (
            <ConversationTraceability conversation={selectedMail} lang={lang || 'en'} />
          )}
        </div>
        <ConfirmationDialog
          open={mobileDeleteDialogOpen}
          onOpenChange={setMobileDeleteDialogOpen}
          title={t('knowledge.delete_confirmation.title')}
          message={t('functions.confirm_delete')}
          description={t('knowledge.delete_confirmation.description')}
          onConfirm={() => { void handleDeleteMobile() }}
          onCancel={() => setMobileDeleteDialogOpen(false)}
          loading={mobileActionsLoading}
          translations={t}
        />
        </div>
      </TooltipProvider>
    )
  }

  // ── MOBILE: vista lista ──
  if (isMobile) {
    return (
      <TooltipProvider delayDuration={0}>
      <div className="flex h-full flex-col">
        <Tabs defaultValue="active" onValueChange={(value) => setActiveTab(value as "active" | "archived")} className="flex h-full flex-col overflow-hidden">

          {/* Single compact bar: [☰] [All|channel filters] [active/archived] [🔍] */}
          <div className="flex items-center gap-1 px-1 py-1 border-b">
            {/* Menu hamburger */}
            <Button
              variant="ghost"
              size="icon-sm"
              className="shrink-0"
              onClick={() => window.dispatchEvent(new Event('rk:open-mobile-menu'))}
            >
              <Menu className="h-4 w-4" />
            </Button>

            {/* Channel filters (All + channels) */}
            <div className="flex-1 min-w-0 overflow-x-auto">
              <PlatformFilters
                selectedPlatform={selectedPlatform}
                onSelectPlatform={setSelectedPlatform}
                channelCatalog={channelCatalog}
                pinnedChannelKeys={pinnedChannelKeys}
                onTogglePinChannelKey={togglePinChannelKey}
                onApplyFilter={(filter) => {
                  log.debug('Applying filter:', filter)
                  setSelectedPlatform(filter.id || 'custom')
                  setActiveFilterChannels(filter.channels)
                  const includesPhoneCalls = filter.channels.includes('Phone Call')
                  const conversationChannels = filter.channels.filter(c => c !== 'Phone Call')
                  applyConversationFilters({
                    tags: filter.tags.join(','),
                    source: conversationChannels.join(','),
                  })
                  if (includesPhoneCalls) {
                    setPhoneCallFilters({
                      tags: filter.tags.join(','),
                      category: filter.user_categories.join(',')
                    })
                  }
                }}
              />
            </div>

            {selectedPlatform === 'whatsapp' && (
              <WhatsAppLineFilter value={selectedLine} onChange={setSelectedLine} />
            )}

            {/* Activas / archivadas: dos pestanas. Estaban dibujadas con dos
                `<button>` crudos dentro de una caja con borde, que es
                exactamente lo que `Tabs` ya hace. */}
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'active' | 'archived')} className="shrink-0">
              <TabsList className="h-auto p-0.5">
                <TabsTrigger value="active" className="gap-1 px-2 py-1 text-xs">
                  <CheckCircle2 className="h-3 w-3" />
                  {activeCount > 0 && <span>{activeCount}</span>}
                </TabsTrigger>
                <TabsTrigger value="archived" className="gap-1 px-2 py-1 text-xs">
                  <Archive className="h-3 w-3" />
                  {archivedCount > 0 && <span>{archivedCount}</span>}
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Search */}
            <Button
              variant="ghost"
              size="icon-sm"
              className="shrink-0"
              onClick={() => {
                setMobileSearchOpen(!mobileSearchOpen)
                if (mobileSearchOpen) setSearchTerm("")
              }}
            >
              <Search className="h-4 w-4" />
            </Button>
          </div>

          {mobileSearchOpen && (
            <div className="bg-background p-3">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('functions.search_conversations')}
                  className="pl-8"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  autoFocus
                />
              </div>
            </div>
          )}

          <div className="relative flex-1 overflow-hidden pt-2">
            {listLoading ? (
              <ConversationListSkeleton itemCount={4} />
            ) : listError ? (
              <div className="flex h-full items-center justify-center">
                <div className="text-center">
                  <p className="mb-2 text-destructive">{t('functions.error_loading_conversations')}</p>
                  <Button variant="outline" size="sm" onClick={() => {
                    refetch()
                    phoneCallsRefetch()
                    if (selectedPlatform === 'betterplace') {
                      void loadBetterplaceListings()
                    }
                  }}>
                    {t('functions.retry')}
                  </Button>
                </div>
              </div>
            ) : mails.length === 0 ? (
              <div className="flex h-full items-center justify-center">
                <p className="text-muted-foreground">{t('functions.no_conversations')}</p>
              </div>
            ) : (
              <ConversationList
                items={mails}
                activeTab={activeTab}
                searchTerm={searchTerm}
                  hasMore={listHasMore}
                  loadingMore={listLoadingMore}
                onLoadMore={handleLoadMore}
                translations={{
                  functions: {
                    loading: t('functions.loading'),
                    retry: t('functions.retry')
                  }
                }}
                onStatusChange={handleStatusChange}
              />
            )}
          </div>
        </Tabs>
      </div>
      </TooltipProvider>
    )
  }

  // ── DESKTOP (sin cambios) ──
  return (
    <div className="flex h-screen">
      <TooltipProvider delayDuration={0}>
        <ResizablePanelGroup
          direction="horizontal"
          className="h-full items-stretch"
        >
          {/* Middle Panel - Conversations List */}
          <ResizablePanel defaultSize={50} minSize={30}>
            <div className="flex h-full flex-col overflow-hidden">
              <Tabs defaultValue="active" onValueChange={(value) => setActiveTab(value as "active" | "archived")} className="flex h-full flex-col overflow-hidden">
                <div className="flex items-center justify-between px-4 py-2">
                  <h1 className="text-xl font-bold">{t('functions.inbox')}</h1>
                </div>

                {/* Simple Local Tabs - No API calls, just local filtering */}
                <div className="px-4 pb-2">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger
                      value="active"
                      onClick={() => setActiveTab('active')}
                      className={activeTab === 'active' ? 'bg-primary text-primary-foreground' : ''}
                    >
                      {t('functions.active')} ({activeCount})
                    </TabsTrigger>
                    <TabsTrigger
                      value="archived"
                      onClick={() => setActiveTab('archived')}
                      className={activeTab === 'archived' ? 'bg-primary text-primary-foreground' : ''}
                    >
                      {t('functions.archived')} ({archivedCount})
                    </TabsTrigger>
                  </TabsList>
                </div>
                <Separator />


                {/* Search Bar */}
                <div className="bg-background p-4">
                  <div className="relative">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder={t('functions.search_conversations')}
                      className="pl-8"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>

                {/* Maintain standard platforms list for checking */}
                {(() => {
                  return (
                    <PlatformFilters
                      selectedPlatform={selectedPlatform}
                      onSelectPlatform={setSelectedPlatform}
                      channelCatalog={channelCatalog}
                      pinnedChannelKeys={pinnedChannelKeys}
                      onTogglePinChannelKey={togglePinChannelKey}
                      onApplyFilter={(filter) => {
                        log.debug('Applying filter:', filter)
                        setSelectedPlatform(filter.id || 'custom')

                        // Store the filter channels to determine data sources
                        setActiveFilterChannels(filter.channels)

                        // Check if filter includes phone calls
                        const includesPhoneCalls = filter.channels.includes('Phone Call')

                        // Filter channels that apply to Conversation model (exclude Phone Call)
                        const conversationChannels = filter.channels.filter(c => c !== 'Phone Call')

                        // Apply filters to backend for conversation-based channels only
                        applyConversationFilters({
                          tags: filter.tags.join(','),
                          source: conversationChannels.join(','),
                          // Add other mappings if needed
                          // category: filter.user_categories.join(',')
                        })

                        // If includes phone calls, trigger phone calls fetch with filters
                        if (includesPhoneCalls) {
                          setPhoneCallFilters({
                            tags: filter.tags.join(','),
                            category: filter.user_categories.join(',')
                          })
                        }
                      }}
                    />
                  )
                })()}

                {selectedPlatform === 'whatsapp' && (
                  <WhatsAppLineFilter value={selectedLine} onChange={setSelectedLine} />
                )}

                {/* Conversations List */}
                <div className="relative flex-1 overflow-hidden pt-4">
                  {listLoading ? (
                    <ConversationListSkeleton itemCount={5} />
                  ) : listError ? (
                    <div className="flex h-full items-center justify-center">
                      <div className="text-center">
                        <p className="mb-2 text-destructive">{t('functions.error_loading_conversations')}</p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            refetch()
                            phoneCallsRefetch()
                            if (selectedPlatform === 'betterplace') {
                              void loadBetterplaceListings()
                            }
                          }}
                        >
                          {t('functions.retry')}
                        </Button>
                      </div>
                    </div>
                  ) : mails.length === 0 ? (
                    <div className="flex h-full items-center justify-center">
                      <div className="text-center">
                        <p className="text-muted-foreground">
                          {t('functions.no_conversations')}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <ConversationList
                      items={mails}
                      activeTab={activeTab}
                      searchTerm={searchTerm}
                      hasMore={listHasMore}
                      loadingMore={listLoadingMore}
                      onLoadMore={handleLoadMore}
                      translations={{
                        functions: {
                          loading: t('functions.loading'),
                          retry: t('functions.retry')
                        }
                      }}
                      onStatusChange={handleStatusChange}
                    />
                  )}
                </div>
              </Tabs>
            </div>
          </ResizablePanel>
          <ResizableHandle withHandle />

          {/* Right Panel - Conversation Detail */}
          <ResizablePanel defaultSize={50} minSize={30}>
            <div className="flex h-full min-w-0 flex-col">
              <div className="min-w-0 border-b">
                {/* `flex-wrap` + `shrink-0`: las pestañas nunca se recortan; si el
                    panel se estrecha, saltan de línea en vez de cortarse. */}
                <div className="flex min-w-0 flex-wrap items-center gap-1 px-2 py-2">
                  {/* Always show the 3 standard panels for all conversations */}
                  {PANELES.map((panel) => {
                    if (panel.id === 'traceability' && isPropertyLeadPlatform) return null
                    return (
                      <BotonDePanel
                        key={panel.id}
                        panel={panel}
                        activo={panelElegido === panel.id}
                        onElegir={() => elegirPanel(panel.id)}
                      />
                    )
                  })}
                </div>
              </div>
              <div className="min-h-0 min-w-0 flex-1 overflow-hidden">
                {/* Always show profile for "Cruces con potenciales vendedores", otherwise show selected view */}
                {(isPropertyLeadPlatform || rightPanelView === "profile") && (
                  <UserProfile
                    mail={selectedMail}
                    translations={t}
                    isPropertyLead={isPropertyLeadPlatform}
                    onOpenConversation={handleOpenRelatedConversation}
                  />
                )}
                {!isPropertyLeadPlatform && rightPanelView === "conversation" && (
                  <ConversationDisplay
                    conversation={selectedMail}
                    translations={t}
                    onDeleted={handleDeleted}
                    onArchived={handleArchived}
                    onActivated={handleActivated}
                    markAsRead={markAsRead}
                    onPhoneCallArchived={() => phoneCallsRefetch()}
                  />
                )}
                {rightPanelView === "feedback" && (
                  <FeedbackForm mail={selectedMail} />
                )}
                {rightPanelView === "instructions" && (
                  <ConversationInstructions mail={selectedMail} />
                )}
                {rightPanelView === "traceability" && (
                  <ConversationTraceability conversation={selectedMail} lang={lang || 'en'} />
                )}
              </div>
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </TooltipProvider>
    </div>
  )
}
